import { AxiosInstance } from 'axios';
import { getAccessToken } from '@/features/auth/tokenStore';
import { redirectToLogin } from '@/features/auth/utils/redirectToLogin';
import { AccountService } from '@/features/auth/services/AccountService';

type RefreshSubscriber = (error: Error | null, token?: string) => void;

let isRefreshing = false;
let refreshSubscribers: RefreshSubscriber[] = [];

function subscribeToRefresh(cb: RefreshSubscriber): void {
  refreshSubscribers.push(cb);
}

function notifyRefreshSubscribers(error: Error | null, token?: string): void {
  refreshSubscribers.forEach((cb) => cb(error, token));
  refreshSubscribers = [];
}

/**
 * Refreshes the access token, deduplicating concurrent calls so only one
 * refresh request is ever in flight at a time. Callers that arrive while a
 * refresh is already running queue on the same in-flight promise.
 * On failure, redirects to the login page and rethrows.
 */
export async function refreshAccessToken(): Promise<string> {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      subscribeToRefresh((refreshError, token) => {
        if (refreshError || !token) {
          reject(refreshError ?? new Error('Refresh failed'));
          return;
        }
        resolve(token);
      });
    });
  }

  isRefreshing = true;

  try {
    const newToken = await AccountService.refresh();
    notifyRefreshSubscribers(null, newToken);
    return newToken;
  } catch (refreshError) {
    notifyRefreshSubscribers(
      refreshError instanceof Error ? refreshError : new Error('Refresh failed')
    );
    void redirectToLogin();
    throw refreshError;
  } finally {
    isRefreshing = false;
  }
}

/**
 * Sets up authentication interceptors on the provided Axios instance:
 * - Injects `Authorization: Bearer <token>` header on every request when a token is available.
 * - On 401 responses, attempts to refresh the access token and retries the original request.
 *   If the refresh itself fails, or if the failing request was already the refresh call,
 *   the user is redirected to the login page.
 */
export function setupAuthInterceptor(axiosInstance: AxiosInstance) {
  axiosInstance.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status !== 401) {
        return Promise.reject(error);
      }

      // A failing refresh request means the refresh token is also invalid — log out immediately.
      if (originalRequest._isRefreshRequest) {
        void redirectToLogin();
        return Promise.reject(error);
      }

      // Don't retry a request that already went through the refresh cycle.
      if (originalRequest._retry) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
  );
}
